import React, { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Person as PersonIcon, Security as SecurityIcon } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrateur',
  coach: 'Coach',
  entreprise: 'Entreprise',
};

const extractError = (error: any, fallback: string): string => {
  const payload = error?.response?.data;
  if (typeof payload?.error === 'string') return payload.error;
  if (typeof payload?.message === 'string') return payload.message;
  if (typeof error?.message === 'string') return error.message;
  return fallback;
};

const Profile: React.FC = () => {
  const { user, setUserFromProfile } = useAuth();
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [companyName, setCompanyName] = useState<string | null>(null);
  const [memberSince, setMemberSince] = useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      setProfileError('');
      try {
        const response = await axios.get('/auth/profile');
        if (cancelled) return;
        const profile = response.data.user;
        setFirstName(profile.firstName || '');
        setLastName(profile.lastName || '');
        setEmail(profile.email || '');
        setRole(profile.role || '');
        setCompanyName(profile.company?.name || null);
        setMemberSince(profile.createdAt || null);
      } catch (error: any) {
        if (!cancelled) {
          setProfileError(extractError(error, 'Impossible de charger le profil.'));
        }
      } finally {
        if (!cancelled) {
          setLoadingProfile(false);
        }
      }
    };

    void loadProfile();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileSuccess('');
    setProfileError('');

    try {
      const response = await axios.put('/auth/profile', { firstName, lastName });
      const updated = response.data.user;
      setFirstName(updated.firstName);
      setLastName(updated.lastName);
      setUserFromProfile(updated);
      setProfileSuccess('Profil mis à jour.');
    } catch (error: any) {
      setProfileError(extractError(error, 'Impossible de mettre à jour le profil.'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordSuccess('');
    setPasswordError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Merci de renseigner tous les champs du mot de passe.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Les nouveaux mots de passe ne correspondent pas.');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    setSavingPassword(true);
    try {
      await axios.put('/auth/change-password', {
        currentPassword,
        newPassword,
      });
      setPasswordSuccess('Mot de passe modifié.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      setPasswordError(extractError(error, 'Impossible de modifier le mot de passe.'));
    } finally {
      setSavingPassword(false);
    }
  };

  if (loadingProfile) {
    return (
      <Container maxWidth="md" sx={{ mt: 6, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  const displayName = `${firstName} ${lastName}`.trim() || user?.email || '';

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Mon profil
        </Typography>
        <Typography color="text.secondary">
          Modifiez vos informations personnelles et votre mot de passe.
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Avatar sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: '1.5rem' }}>
              {firstName?.charAt(0)}
              {lastName?.charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {email}
              </Typography>
              <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                <Chip
                  label={ROLE_LABELS[role] || role}
                  size="small"
                  color={role === 'admin' ? 'error' : role === 'entreprise' ? 'secondary' : 'primary'}
                />
                {companyName && (
                  <Chip label={companyName} size="small" variant="outlined" />
                )}
              </Stack>
              {memberSince && (
                <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                  Compte créé le {new Date(memberSince).toLocaleDateString('fr-FR')}
                </Typography>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <PersonIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Informations personnelles
            </Typography>
          </Stack>

          {profileSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {profileSuccess}
            </Alert>
          )}
          {profileError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {profileError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleProfileUpdate}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Prénom"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={savingProfile}
                />
                <TextField
                  fullWidth
                  required
                  label="Nom"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={savingProfile}
                />
              </Stack>
              <TextField
                fullWidth
                label="Adresse e-mail"
                value={email}
                disabled
                helperText="L’adresse e-mail ne peut pas être modifiée ici."
              />
              <TextField
                fullWidth
                label="Rôle"
                value={ROLE_LABELS[role] || role}
                disabled
                helperText="Le rôle est géré par un administrateur."
              />
              {role === 'entreprise' && (
                <TextField
                  fullWidth
                  label="Entreprise"
                  value={companyName || '—'}
                  disabled
                  helperText="L’entreprise liée à votre compte ne peut pas être modifiée ici."
                />
              )}
            </Stack>
            <Button
              type="submit"
              variant="contained"
              sx={{ mt: 3 }}
              disabled={savingProfile}
            >
              {savingProfile ? 'Enregistrement…' : 'Enregistrer le profil'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <SecurityIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Mot de passe
            </Typography>
          </Stack>

          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {passwordSuccess}
            </Alert>
          )}
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}

          <Box component="form" onSubmit={handlePasswordChange}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                required
                type="password"
                label="Mot de passe actuel"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={savingPassword}
                autoComplete="current-password"
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  required
                  type="password"
                  label="Nouveau mot de passe"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={savingPassword}
                  autoComplete="new-password"
                />
                <TextField
                  fullWidth
                  required
                  type="password"
                  label="Confirmer le mot de passe"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={savingPassword}
                  autoComplete="new-password"
                />
              </Stack>
            </Stack>
            <Button
              type="submit"
              variant="contained"
              color="warning"
              sx={{ mt: 3 }}
              disabled={savingPassword}
            >
              {savingPassword ? 'Modification…' : 'Modifier le mot de passe'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Profile;
